import React from "react";
import siteConfig from "../config/siteConfig";
import PolicyPage from "./PolicyPage";
import "../css/privacy.scss";

function Privacy() {
  const sections = [
    {
      id: "scope-controller",
      title: "Scope and data controller",
      paragraphs: [
        `This notice explains how ${siteConfig.company.legalName} collects, uses, shares and protects personal data when you visit the ${siteConfig.brandName} store, place an order or contact us. It applies to information handled through this website and related customer-care communications.`,
        `${siteConfig.company.legalName}, at ${siteConfig.contact.address}, is the data user responsible for that information. Privacy questions and requests may be sent to ${siteConfig.contact.email}.`,
      ],
    },
    {
      id: "data-collected",
      title: "Information we collect",
      paragraphs: [
        "The information we collect depends on how you use the store. We limit collection to information that is relevant to the purposes described below.",
      ],
      items: [
        "Identity and contact details, including your name, email address, delivery address, billing address and any company name you provide.",
        "Order and transaction details, including products, sizes, colours, amounts, currency, delivery selection, order reference and payment status.",
        "Customer-care records, including messages, return requests, photographs supplied for a product issue and our replies.",
        "Technical and security information, such as IP address, browser, device information, timestamps, referral pages, diagnostic events and fraud-prevention signals where generated by our hosting or payment services.",
        "Shopping preferences stored on your device, including the contents of your bag and wishlist.",
        "Marketing preferences and engagement information where you have chosen to receive direct marketing.",
      ],
    },
    {
      id: "collection-sources",
      title: "How information is collected",
      paragraphs: [
        "We receive information directly from you when you complete checkout, contact customer care or request a return. We may also receive transaction status from a payment provider, delivery events from a carrier and technical information automatically from the systems used to operate and secure the store.",
        "If you give us personal data about another person, such as a gift recipient, you must have permission to do so and should make this notice available to them.",
      ],
    },
    {
      id: "purposes",
      title: "Why we use personal data",
      paragraphs: [
        "We use personal data for the purposes disclosed when it is collected or for directly related purposes, including the following:",
      ],
      items: [
        "To take payment, confirm and fulfil an order, arrange delivery, and administer returns, refunds and product issues.",
        "To answer enquiries, maintain customer-care records and resolve complaints or disputes.",
        "To detect abuse, verify transactions, prevent fraud, maintain service security and troubleshoot faults.",
        "To meet accounting, tax, product-safety, recordkeeping and other legal obligations.",
        "To understand store performance and improve products and accessibility using proportionate, aggregated or de-identified information where possible.",
        "To send direct marketing only after any notice and consent required by applicable law, and to maintain a suppression record after an opt-out.",
      ],
    },
    {
      id: "lawful-bases",
      title: "Legal grounds",
      paragraphs: [
        "Depending on the law that applies, we process information because it is necessary to perform a contract with you, to take steps you request before a contract, to comply with a legal duty, for a legitimate business interest that is not overridden by your rights, or because you have given consent. Where processing relies on consent, you may withdraw it for future use at any time.",
        "Under Hong Kong's Personal Data (Privacy) Ordinance, personal data is used for the collection purpose or a directly related purpose unless your prescribed consent is obtained or another legal exception applies.",
      ],
    },
    {
      id: "payments",
      title: "Payments and fraud checks",
      paragraphs: [
        "Card details are entered on the secure page operated by our payment partner and are not intended to be stored by this storefront. The provider sends us the information needed to confirm the result of the transaction, such as the amount, currency, transaction reference and payment status.",
        "We and relevant providers may use order, device and payment signals to identify unusual activity, verify an order or refuse a transaction where reasonably necessary to protect customers and the store.",
      ],
    },
    {
      id: "marketing",
      title: "Direct marketing",
      paragraphs: [
        `We do not use your personal data for direct marketing without the notice and consent required by applicable law. If you opt in, communications may concern ${siteConfig.brandName} clothing, accessories, store news and promotions. We do not provide personal data to another business for its own direct marketing without the required consent.`,
        `You may withdraw or change a marketing choice at any time, without charge, by using an unsubscribe control in the message or emailing ${siteConfig.contact.email}. Transactional order and service messages are not marketing and may still be sent when necessary.`,
      ],
    },
    {
      id: "cookies-storage",
      title: "Cookies and local storage",
      paragraphs: [
        "The storefront uses browser local storage to keep your bag and wishlist on your device. It does not intentionally set optional analytics or advertising cookies in its current configuration. If that changes, the cookie policy will identify the technology and any consent controls that apply.",
      ],
    },
    {
      id: "sharing",
      title: "Who receives information",
      paragraphs: [
        "We disclose only the information needed for a specific service or lawful purpose. Recipients may include website hosting and security providers, payment and fraud-prevention providers, fulfilment partners, delivery carriers, customer-support tools, accountants, insurers, professional advisers and public authorities where disclosure is legally required.",
        "Service providers are expected to use personal data only on documented instructions or for the service they independently provide, protect it appropriately and keep it only as long as necessary. We may transfer relevant records in a merger, financing, reorganisation or sale, subject to appropriate confidentiality and legal safeguards.",
      ],
    },
    {
      id: "international-transfers",
      title: "Cross-border transfers",
      paragraphs: [
        "Our store serves customers in multiple locations, so information may be processed outside the place where it was collected. Where required, we use contractual, organisational or other recognised safeguards and assess the recipient and destination before transferring personal data.",
      ],
    },
    {
      id: "retention",
      title: "Retention",
      paragraphs: [
        "We keep personal data only for as long as necessary for the purpose for which it was collected and for applicable legal, accounting, warranty and dispute periods. Order and transaction records may be retained for up to seven years where required for business and statutory records. Customer-care records are kept only as long as needed to resolve the matter and establish an appropriate audit trail.",
        "Local bag and wishlist data remains on your device until it is removed through the store or your browser controls. When information is no longer required, we delete it, anonymise it or securely isolate it until deletion is possible.",
      ],
    },
    {
      id: "security",
      title: "Security",
      paragraphs: [
        "We use proportionate technical and organisational measures designed to protect personal data, including controlled access, secure transmission where supported, service-provider review and incident procedures. No internet service can guarantee absolute security. Please do not send payment-card details or account passwords by email.",
      ],
    },
    {
      id: "rights",
      title: "Your choices and rights",
      paragraphs: [
        `You may ask whether we hold your personal data and request access to or correction of it under the Hong Kong Personal Data (Privacy) Ordinance. Depending on where you live, you may also have rights to deletion, restriction, objection, portability, withdrawal of consent and complaint to a privacy regulator. Email ${siteConfig.contact.email} and describe your request.`,
        "We may need to verify your identity and clarify the scope of a request. We will respond within the period required by applicable law. A lawful fee may be charged for a data-access request where permitted, and we will explain any legal reason that prevents us from fulfilling all or part of a request.",
      ],
    },
    {
      id: "children",
      title: "Children",
      paragraphs: [
        "This store is intended for adults and is not directed to children. A person who cannot lawfully enter a purchase contract in their location should use the store only with the involvement of a parent or legal guardian. If you believe a child has provided personal data without appropriate permission, please contact us so it can be reviewed and deleted where required.",
      ],
    },
    {
      id: "changes-contact",
      title: "Changes and contact",
      paragraphs: [
        `We may update this notice when our services or legal obligations change. The review date at the top identifies the current version. Material changes will be highlighted where appropriate. Questions, privacy requests and complaints may be sent to ${siteConfig.contact.email} or to ${siteConfig.contact.address}.`,
      ],
    },
  ];

  return (
    <div className="privacy">
      <PolicyPage
        eyebrow="Legal"
        title="Privacy policy"
        intro={`How ${siteConfig.brandName} collects, uses, shares and protects personal data.`}
        sections={sections}
        relatedLinks={[
          { label: "Cookie policy", to: "/cookies" },
          { label: "Security and fraud", to: "/security" },
          { label: "All policies", to: "/policies" },
          { label: "Contact", to: "/contact" },
        ]}
      />
    </div>
  );
}

export default Privacy;
